import React, { useState, useCallback } from "react"
import { useNavigate, Link } from "react-router-dom"
import {
  useQuarterlyReports,
  useDeleteHouseholdQuarterlyReport,
  useRegenerateHouseholdQuarterlyReport,
  useSendHouseholdQuarterlyReport,
} from "~/api/quarterly_reports"
import {
  useGenerateReports,
  useUpdateCommunication,
} from "~/api/communications"
import { useHouseholds } from "~/api/household"
import { generatorPath, reportShowPath } from "@/QuarterlyReportApp"
import {
  Settings,
  Eye,
  Pencil,
  WandSparkles,
  Trash,
  FileText,
} from "lucide-react"
import { Button } from "@/ui/Button"
import { Badge } from "@/ui/Badge"
import useDebouncedString from "@/hooks/useDebouncedString"
import QuarterlyReportLayout from "./QuarterlyReportLayout"
import { useCommunicationContext } from "./CommunicationContext"

const QuarterlyReportIndex: React.FC = () => {
  const { communicationId, communication } = useCommunicationContext()
  const navigate = useNavigate()
  const updateCommunicationMutation = useUpdateCommunication()
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<string>("")

  const { data, isLoading, error } = useQuarterlyReports(communicationId, {
    refetchInterval: (data) => (data?.all_ready === false ? 3000 : false),
  })
  const { data: householdsData, isLoading: isLoadingHouseholds } =
    useHouseholds()
  const generateReportsMutation = useGenerateReports()
  const deleteReportMutation = useDeleteHouseholdQuarterlyReport()
  const regenerateReportMutation = useRegenerateHouseholdQuarterlyReport()
  const sendReportMutation = useSendHouseholdQuarterlyReport()

  const availableHouseholds = React.useMemo(() => {
    if (!householdsData || !data?.reports) {
      return []
    }

    return householdsData.filter(
      (household) =>
        !data.reports.some((report) => report.household_id === household.id),
    )
  }, [householdsData, data?.reports])

  const handleAddHousehold = () => {
    if (selectedHouseholdId && communicationId) {
      generateReportsMutation.mutate(
        {
          communicationId,
          householdIds: [selectedHouseholdId],
        },
        {
          onSuccess: () => {
            setSelectedHouseholdId("")
          },
        },
      )
    }
  }

  const handlePreview = (reportId: string) => {
    sendReportMutation.mutate(reportId, {
      onSuccess: () => {
        console.log("PDF generated successfully and opened in new tab")
      },
      onError: (error: Error) => {
        console.error("Failed to generate PDF:", error)
        alert(`Failed to generate PDF: ${error.message}`)
      },
    })
  }

  const handleEdit = (reportId: string) => {
    navigate(reportShowPath({ communicationId, reportId }))
  }

  const handleRegenerate = (reportId: string) => {
    regenerateReportMutation.mutate(reportId, {
      onSuccess: () => {
        // Success is handled by the mutation's onSuccess callback
        // which invalidates the query
      },
      onError: (error) => {
        console.error("Failed to regenerate report:", error)
        alert("Failed to regenerate report.")
      },
    })
  }

  const handleDelete = (reportId: string) => {
    if (confirm("Are you sure you want to delete this report?")) {
      deleteReportMutation.mutate(reportId, {
        onSuccess: () => {
          // Success is handled by the mutation's onSuccess callback
          // which invalidates the query
        },
        onError: (error) => {
          console.error("Failed to delete report:", error)
          alert("Failed to delete report.")
        },
      })
    }
  }

  const updateMessageBody = useCallback(
    (body: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        body: body,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const updateMessageFooter = useCallback(
    (footer: string) => {
      updateCommunicationMutation.mutate({
        id: communicationId,
        footer: footer,
      })
    },
    [communicationId, updateCommunicationMutation],
  )

  const { value: messageBody, setValue: setMessageBody } = useDebouncedString({
    initialValue: communication?.body || "",
    onUpdate: updateMessageBody,
    debounceMs: 500,
    shouldUpdate: !!communication,
  })

  const { value: messageFooter, setValue: setMessageFooter } =
    useDebouncedString({
      initialValue: communication?.footer || "",
      onUpdate: updateMessageFooter,
      debounceMs: 500,
      shouldUpdate: !!communication,
    })

  const handleSendReport = () => {
    // Get the first household report
    const firstReport = data?.reports[0]
    if (firstReport) {
      sendReportMutation.mutate(firstReport.id, {
        onSuccess: () => {
          console.log("PDF generated successfully and opened in new tab")
        },
        onError: (error: Error) => {
          console.error("Failed to generate PDF:", error)
          alert(`Failed to generate PDF: ${error.message}`)
        },
      })
    }
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading quarterly reports...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load quarterly reports: {error.message}
      </div>
    )
  }

  return (
    <QuarterlyReportLayout
      title="Quarterly Reports"
      subtitle="View and manage quarterly reports for households"
      actions={
        <Button
          size="md"
          variant="outline"
          onClick={() => navigate(generatorPath({ communicationId }))}
          icon={<Settings size={18} />}
        >
          Edit Report Settings
        </Button>
      }
    >
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Household</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.reports.length === 0 ? (
              <tr>
                <td colSpan={2} className="text-center">
                  No reports generated yet. Return to the report generator to
                  create reports.
                </td>
              </tr>
            ) : (
              data?.reports.map((report) => (
                <tr key={report.id}>
                  <td>
                    <Link
                      to={reportShowPath({
                        communicationId,
                        reportId: report.id,
                      })}
                    >
                      {report.household_name}
                    </Link>
                    {report.status !== "completed" && (
                      <Badge
                        variant={
                          report.status === "generating"
                            ? "generating"
                            : report.status === "failed"
                            ? "failed"
                            : "pending"
                        }
                        pulse={report.status === "generating"}
                        className="ms-2"
                      >
                        {report.status === "generating"
                          ? "Generating..."
                          : report.status === "failed"
                          ? "Failed"
                          : "Pending"}
                      </Badge>
                    )}
                  </td>
                  <td>
                    <div className="table-actions">
                      <Button
                        variant="filled"
                        size="sm"
                        onClick={() => handlePreview(report.id)}
                        icon={<Eye size={18} />}
                        disabled={
                          report.status !== "completed" ||
                          sendReportMutation.isLoading
                        }
                      >
                        {sendReportMutation.isLoading
                          ? "Generating..."
                          : "Preview"}
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(report.id)}
                        icon={<Pencil size={18} />}
                        disabled={report.status !== "completed"}
                      >
                        Edit
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegenerate(report.id)}
                        icon={<WandSparkles size={18} />}
                        disabled={report.status !== "completed"}
                      >
                        Regenerate
                      </Button>

                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(report.id)}
                        icon={<Trash size={18} />}
                        aria-label="Delete report"
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isLoadingHouseholds ? (
        <div className="add-household-container">
          <div className="alert alert-info">
            Loading available households...
          </div>
        </div>
      ) : availableHouseholds.length > 0 ? (
        <div className="add-household">
          <select
            className="form-select"
            value={selectedHouseholdId}
            onChange={(e) => setSelectedHouseholdId(e.target.value)}
            disabled={generateReportsMutation.isLoading}
          >
            <option value="">Select household</option>
            {availableHouseholds.map((household) => (
              <option key={household.id} value={household.id}>
                {household.name}
              </option>
            ))}
          </select>
          <Button
            size="md"
            variant="filled"
            onClick={handleAddHousehold}
            disabled={!selectedHouseholdId || generateReportsMutation.isLoading}
          >
            {generateReportsMutation.isLoading ? "Adding..." : "Add Household"}
          </Button>
          {generateReportsMutation.isError && (
            <div className="alert alert-danger mt-2">
              Failed to add household:{" "}
              {(generateReportsMutation.error as Error)?.message}
            </div>
          )}
        </div>
      ) : (
        !isLoadingHouseholds && (
          <div className="add-household-container mt-4">
            All households already have reports generated.
          </div>
        )
      )}

      <div className="additional-settings">
        <div className="email-additional-settings-header">
          <div className="subject">subject line: </div>
          <h3 className="additional-settings-heading">
            [Household Name] [Year] [Quarter] Report
          </h3>
        </div>

        <div className="additional-settings-section">
          <label htmlFor="messageBody">Message Body</label>
          <textarea
            id="messageBody"
            value={messageBody}
            onChange={(e) => {
              setMessageBody(e.target.value)
            }}
            placeholder="Enter message body..."
            rows={3}
          />
        </div>

        <div className="additional-settings-section">
          <label htmlFor="messageFooter">Footer</label>
          <textarea
            id="messageFooter"
            value={messageFooter}
            onChange={(e) => {
              setMessageFooter(e.target.value)
            }}
            placeholder="Enter footer..."
            rows={3}
          />
        </div>

        <div className="additional-settings-section">
          <Button
            size="md"
            variant="filled"
            onClick={handleSendReport}
            disabled={
              !data?.reports ||
              data.reports.length === 0 ||
              data.reports[0]?.status !== "completed" ||
              sendReportMutation.isLoading
            }
            icon={<FileText size={18} />}
          >
            {sendReportMutation.isLoading ? "Generating PDF..." : "Send Report"}
          </Button>
          {sendReportMutation.isError && (
            <div className="alert alert-danger mt-2">
              Failed to generate PDF:{" "}
              {(sendReportMutation.error as Error)?.message}
            </div>
          )}
        </div>
      </div>
    </QuarterlyReportLayout>
  )
}

export default QuarterlyReportIndex
